declare global {
  interface Window {
    braze?: {
      wipeData: () => void
      openSession: () => void
      changeUser: (userId: string) => string
      subscribeToBannersUpdates: (callback: (banners: object) => void) => string
      requestBannersRefresh: (
        bannerIds: string[],
        onSuccess?: () => void,
        onError?: () => void
      ) => void
      insertBanner: (banner: object, container: HTMLElement) => void
      isInitialized: () => boolean
      removeSubscription: (subscriptionId: string) => void
      getUser: () => {
        addAlias: (alias: string, label: string) => boolean
        getUserId: () => string
        setCustomUserAttribute: (attribute: string, value: string) => void
      }
      getDeviceId: () => string
      requestImmediateDataFlush: (
        callback?: (isSuccess: boolean) => void
      ) => void
      requestContentCardsRefresh: (
        onSuccess?: () => void,
        onError?: () => void
      ) => void
    }
    brazeBanners: object
    brazeBannerPlacementIds: string[]
  }
}

const wait = (milliseconds: number) => {
  return new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      clearTimeout(timeout)
      resolve()
    }, milliseconds)
  })
}

const DATA_PUSH_INTERVAL_MS = 1000
const DATA_PUSH_MAX_ATTEMPTS = 5

export class Braze {
  static changeUser = (brazeUserId: string) => {
    window.braze?.changeUser(brazeUserId)
  }

  static anonymize = () => {
    window.braze?.wipeData()
    Braze.openSession()
  }

  static refreshBanners = () => {
    return new Promise<void>((resolve, reject) => {
      window.braze?.requestBannersRefresh(
        window.brazeBannerPlacementIds || [],
        () => {
          resolve()
        },
        () => {
          reject()
        }
      )
    })
  }

  static renderBanner = (banner: object, containerElem: HTMLElement) => {
    window.braze?.insertBanner(banner, containerElem)
  }

  static isInitialized = () => {
    return window.braze?.isInitialized() || false
  }

  static subscribeToBannerUpdates = (callback: (banners: object) => void) => {
    return window.braze?.subscribeToBannersUpdates(callback)
  }

  static removeSubscription = (subscriptionId: string) => {
    window.braze?.removeSubscription(subscriptionId)
  }

  static openSession = () => {
    const deviceId = window.braze?.getDeviceId()
    window.braze?.openSession()
    window.braze?.getUser()?.addAlias(deviceId, 'deviceId')
  }

  static addAlias = (value: string, label: string) => {
    window.braze?.getUser()?.addAlias(value, label)
  }

  static setDeviceIdAlias = () => {
    const externalId = window.braze?.getUser()?.getUserId()

    if (!externalId) {
      const deviceId = window.braze?.getDeviceId()
      window.braze?.getUser()?.addAlias(deviceId, 'deviceId')
    }
  }

  static pushData = () => {
    return new Promise<boolean>((resolve) => {
      window.braze?.requestImmediateDataFlush?.((isSuccess) => {
        resolve(isSuccess)
      })
    })
  }

  static pushDataUntilSuccessful = () => {
    let attempts = 0

    const attemptPush = async () => {
      attempts++
      const isSuccess = await Braze.pushData()

      if (isSuccess) {
        return true
      }

      if (attempts >= DATA_PUSH_MAX_ATTEMPTS) {
        return false
      }

      await wait(DATA_PUSH_INTERVAL_MS)
      await attemptPush()
    }

    return attemptPush()
  }
}
